import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { Loader2, Check, ShieldCheck, Lock, Plug } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const SCENARIOS = [
  {
    t: "Understands and replies",
    d: "We read what each customer actually means and reply in seconds, in your voice, from your own knowledge.",
    customer: "Hi, are you open this Sunday?",
    steps: ["Reading the message", "Checking your opening hours", "Writing a reply"],
    reply: "We are. Open Sunday, 10am to 4pm. Want me to save you a slot?",
  },
  {
    t: "Takes action across your tools",
    d: "Bookings, orders, refunds, updates. We connect to the tools you already run and do the real work, not just the talking.",
    customer: "Can I book a deep clean for Friday morning?",
    steps: ["Checking the calendar", "Finding a Friday slot", "Creating the booking", "Sending a confirmation"],
    reply: "Booked for Friday, 9am. Added to the team calendar and sent you a confirmation.",
  },
  {
    t: "Delegates and escalates",
    d: "For anything that needs a human touch, we find the right team, the right person, and the right time, and bring in your people only when it truly matters.",
    customer: "I think I was overcharged this month.",
    steps: ["Understanding the issue", "Choosing the right team", "Opening a ticket and assigning it", "Setting a callback time"],
    reply: "We have routed this to billing and opened a ticket. Priya, who handles refunds, will call you back by 2pm today.",
  },
  {
    t: "Manages the whole job end to end",
    d: "Like a personal assistant, we juggle the moving parts, update your systems, keep everyone in the loop, and make sure nothing is dropped.",
    customer: "Move my Friday clean to Saturday and update my address.",
    steps: ["Finding your Friday booking", "Moving it to Saturday 9am", "Updating your address on file", "Telling your cleaner the change", "Confirming with you"],
    reply: "Done. Your clean is now Saturday 9am at the new address, and your cleaner has been told.",
  },
  {
    t: "Tailored to every customer",
    d: "We remember each person's history and what they like, so every reply feels personal, never copy and paste.",
    customer: "Hey, it is Sam again.",
    hasHistory: true,
    steps: ["Recognising Sam", "Recalling past visits", "Loading preferences"],
    reply: "Welcome back, Sam. Same address on Oak Street, and your usual deep clean?",
  },
];

const bubbleStyle = (isWappy) => ({
  alignSelf: isWappy ? "flex-start" : "flex-end",
  maxWidth: "85%",
  padding: "8px 12px",
  fontSize: 13.5,
  lineHeight: 1.45,
  fontWeight: 300,
  background: isWappy ? "var(--accent)" : "#202c33",
  color: isWappy ? "#0c1605" : "var(--text)",
  border: "none",
  borderRadius: isWappy ? "0 10px 10px 10px" : "10px 0 10px 10px",
});

const waWallpaper = `url("data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280' viewBox='0 0 280 280'><g fill='none' stroke='#ffffff' stroke-opacity='0.035' stroke-width='2'><circle cx='50' cy='50' r='15'/><circle cx='44' cy='47' r='1.5'/><circle cx='56' cy='47' r='1.5'/><path d='M43 56 q7 6 14 0'/><path d='M205 42 q-9 -11 -18 0 q-9 11 18 26 q27 -15 18 -26 q-9 -11 -18 0'/><path d='M135 118 l5 16 17 0 -14 11 5 17 -13 -10 -13 10 5 -17 -14 -11 17 0 z'/><rect x='40' y='195' width='40' height='28'/><path d='M40 195 l20 15 20 -15'/><rect x='212' y='190' width='24' height='42'/><path d='M216 226 h16'/><path d='M150 246 q0 -20 17 -22 M167 224 v20 a7 7 0 1 1 -7 -7'/></g></svg>"
)}")`;

function AgentDemo({ idx, setIdx }) {
  const [customerShown, setCustomerShown] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [replyShown, setReplyShown] = useState(false);
  const s = SCENARIOS[idx];

  useEffect(() => {
    setCustomerShown(false);
    setActiveStep(-1);
    setReplyShown(false);
    const timers = [];
    timers.push(setTimeout(() => setCustomerShown(true), 350));
    let t = 1100;
    s.steps.forEach((_, i) => {
      timers.push(setTimeout(() => setActiveStep(i), t));
      t += 850;
    });
    timers.push(setTimeout(() => setActiveStep(s.steps.length), t));
    t += 450;
    timers.push(setTimeout(() => setReplyShown(true), t));
    t += 3200;
    timers.push(setTimeout(() => setIdx((p) => (p + 1) % SCENARIOS.length), t));
    return () => timers.forEach(clearTimeout);
  }, [idx, setIdx, s.steps]);

  const stepsDone = activeStep >= s.steps.length;

  return (
    <div
      data-testid="agent-demo"
      style={{
        width: "100%",
        maxWidth: 400,
        background: "#0d1217",
        border: "1px solid var(--border-strong)",
        overflow: "hidden",
        boxShadow: "0 24px 60px -24px rgba(0,0,0,0.7)",
      }}
    >
      {/* window chrome */}
      <div className="flex items-center gap-2 px-4" style={{ height: 38, background: "#11171e", borderBottom: "1px solid var(--border)" }}>
        <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#ff5f57" }} />
        <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#febc2e" }} />
        <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#28c840" }} />
        <span className="ml-3 text-xs" style={{ color: "var(--text-muted)", fontWeight: 300 }}>WhatsApp · Your business</span>
      </div>

      {/* conversation */}
      <div className="flex flex-col gap-2.5 px-4 py-5" style={{ minHeight: 480, backgroundColor: "#0b141a", backgroundImage: waWallpaper, backgroundSize: "200px" }}>
        {s.hasHistory && (
          <motion.div key={`h-${idx}`} initial={{ opacity: 0 }} animate={{ opacity: 0.55 }} transition={{ duration: 0.4 }} className="flex flex-col gap-2 mb-1">
            <div className="self-center text-xs px-2.5 py-1" style={{ color: "var(--text-muted)", background: "rgba(255,255,255,0.05)", borderRadius: 8 }}>Earlier in this chat</div>
            <div className="self-start" style={{ width: "62%", height: 32, background: "rgba(255,255,255,0.05)", borderRadius: "0 10px 10px 10px" }} />
            <div className="self-end" style={{ width: "44%", height: 22, background: "rgba(255,255,255,0.05)", borderRadius: "10px 0 10px 10px" }} />
          </motion.div>
        )}
        {customerShown && (
          <motion.div key={`c-${idx}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={bubbleStyle(false)}>
            {s.customer}
          </motion.div>
        )}

        {activeStep >= 0 && (
          <motion.div key={`t-${idx}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="self-start" style={{ width: "100%", maxWidth: "92%", padding: "2px 0" }}>
            <div className="flex items-center gap-2 mb-2.5">
              {stepsDone
                ? <Check size={13} strokeWidth={2.6} style={{ color: "var(--accent)" }} />
                : <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--accent)" }} className="animate-pulse" />}
              <span className="text-xs" style={{ color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {stepsDone ? "What Wappy did" : "Wappy is working"}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              {s.steps.map((st, i) => {
                if (i > activeStep) return null;
                const done = i < activeStep || stepsDone;
                return (
                  <motion.div key={st} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22 }}
                    className="flex items-center gap-2 text-sm" style={{ color: done ? "var(--text-2)" : "var(--text)", fontWeight: 300 }}>
                    {done
                      ? <Check size={13} style={{ color: "var(--accent)", flexShrink: 0 }} strokeWidth={2.4} />
                      : <Loader2 size={13} className="animate-spin" style={{ color: "var(--accent)", flexShrink: 0 }} />}
                    {st}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {replyShown && (
          <motion.div key={`r-${idx}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} style={bubbleStyle(true)}>
            {s.reply}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default function Landing() {
  const [demoIdx, setDemoIdx] = useState(0);
  return (
    <div style={{ background: "var(--canvas)", minHeight: "100vh", padding: "0 clamp(16px, 5vw, 96px)" }}>
      <div
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          borderLeft: "1px solid var(--border)",
          borderRight: "1px solid var(--border)",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 md:px-12 h-20" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2.5">
            <div style={{ width: 20, height: 20, background: "var(--accent)" }} />
            <span className="font-medium tracking-tight">Wappy</span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <SignedOut>
              <Link to="/sign-in" style={{ color: "var(--text-2)" }} data-testid="landing-signin">Sign in</Link>
              <Link to="/sign-up" className="btn btn-accent btn-sm" data-testid="landing-signup">Get started</Link>
            </SignedOut>
            <SignedIn>
              <Link to="/app" className="btn btn-accent btn-sm" data-testid="landing-dashboard">Go to dashboard</Link>
            </SignedIn>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 md:px-12 py-24 md:py-36" style={{ borderBottom: "1px solid var(--border)" }}>
          <motion.h1 {...fadeUp}
            className="tracking-tight"
            style={{ fontWeight: 200, fontSize: "clamp(2.2rem, 4.6vw, 3.6rem)", lineHeight: 1.1, color: "var(--text)", maxWidth: 800 }}>
            The AI agent that runs and manages your business on WhatsApp.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}
            className="mt-6 text-base md:text-lg" style={{ color: "var(--text-muted)", fontWeight: 300, lineHeight: 1.65, maxWidth: 580 }}>
            You always wanted a bigger team, sharper tools, and an expert who never logs off.
            That is us. We understand your customers, answer them, take action across your
            tools, and manage the whole front of your business. Whatever you do.
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.18 }} className="mt-10 flex items-center gap-5 flex-wrap">
            <Link to="/sign-up" className="btn btn-accent" data-testid="hero-cta" style={{ height: 48, paddingLeft: 26, paddingRight: 26 }}>
              Get started
            </Link>
            <span className="text-sm" style={{ color: "var(--text-muted)", fontWeight: 300 }}>No code. Set up in minutes.</span>
          </motion.div>
        </section>

        {/* Trust strip */}
        <div className="px-6 md:px-12 py-5 flex flex-wrap items-center gap-x-9 gap-y-3" style={{ borderBottom: "1px solid var(--border)" }}>
          {[
            { Icon: ShieldCheck, t: "Official WhatsApp Business Platform" },
            { Icon: Lock, t: "Your data stays yours, encrypted" },
            { Icon: Plug, t: "Connects to the tools you already use" },
          ].map(({ Icon, t }) => (
            <div key={t} className="flex items-center gap-2.5 text-sm" style={{ color: "var(--text-muted)", fontWeight: 300 }}>
              <Icon size={15} strokeWidth={1.6} style={{ color: "var(--accent)" }} />
              {t}
            </div>
          ))}
        </div>

        {/* What it does, shown as a live conversation */}
        <section id="demo" style={{ borderBottom: "1px solid var(--border)" }}>
          <motion.div {...fadeUp} className="px-6 md:px-12 pt-16 pb-10" style={{ borderBottom: "1px solid var(--border)" }}>
            <h2 className="tracking-tight" style={{ fontWeight: 200, fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "var(--text)", lineHeight: 1.18, maxWidth: 640 }}>
              Wappy becomes the face of your business on WhatsApp.
            </h2>
            <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-muted)", fontWeight: 300, lineHeight: 1.6, maxWidth: 600 }}>
              We do not just reply. We understand, decide, and act. Watch us work through a
              real request, step by step.
            </p>
          </motion.div>

          <div className="px-6 md:px-12 py-12 grid gap-10 lg:gap-14" style={{ gridTemplateColumns: "1fr", alignItems: "start" }}>
            <div className="grid lg:grid-cols-[1fr_400px] gap-10 lg:gap-16 items-stretch">
              {/* Left: feature list */}
              <div className="flex flex-col justify-between">
                {SCENARIOS.map((sc, i) => {
                  const active = i === demoIdx;
                  return (
                    <button
                      key={sc.t}
                      onClick={() => setDemoIdx(i)}
                      data-testid={`feature-item-${i}`}
                      className="text-left transition-all"
                      style={{
                        padding: "16px 0 16px 18px",
                        borderLeft: `2px solid ${active ? "var(--accent)" : "var(--border)"}`,
                        opacity: active ? 1 : 0.5,
                        cursor: "pointer",
                        background: "transparent",
                      }}
                    >
                      <div className="text-base md:text-lg" style={{ fontWeight: 400, color: active ? "var(--text)" : "var(--text-2)" }}>{sc.t}</div>
                      <AnimatePresence initial={false}>
                        {active && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }} className="overflow-hidden">
                            <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)", fontWeight: 300, lineHeight: 1.55 }}>{sc.d}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  );
                })}
              </div>

              {/* Right: animated chat window */}
              <AgentDemo idx={demoIdx} setIdx={setDemoIdx} />
            </div>
          </div>
        </section>

        {/* North star numbers */}
        <section className="px-6 md:px-12 py-16" style={{ borderBottom: "1px solid var(--border)" }}>
          <motion.p {...fadeUp} className="text-sm mb-10" style={{ color: "var(--text-muted)", fontWeight: 300, letterSpacing: "0.04em" }}>
            Faster, more reliable, and always on
          </motion.p>
          <div className="grid gap-10" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))" }}>
            {[
              { v: "10x", l: "faster replies than a person, on every single message" },
              { v: "100%", l: "of messages answered, never missed" },
              { v: "24/7", l: "working every day, no breaks or sick days" },
              { v: "70%", l: "of the work handled without a person" },
            ].map((s, i) => (
              <motion.div key={s.l} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 4) * 0.06 }}>
                <div style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.2rem)", fontWeight: 200, color: "var(--accent)", lineHeight: 1 }}>{s.v}</div>
                <div className="mt-3 text-sm" style={{ color: "var(--text-2)", fontWeight: 300, lineHeight: 1.5, maxWidth: 200 }}>{s.l}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why it matters for you */}
        <section className="px-6 md:px-12 py-20" style={{ borderBottom: "1px solid var(--border)" }}>
          <motion.div {...fadeUp}>
            <h2 className="tracking-tight" style={{ fontWeight: 200, fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "var(--text)", lineHeight: 1.18, maxWidth: 640 }}>
              Why run your business on Wappy
            </h2>
            <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-muted)", fontWeight: 300, lineHeight: 1.6, maxWidth: 600 }}>
              More customers, less work, and lower cost. Here is what changes for you.
            </p>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-px" style={{ background: "var(--border)" }}>
            {[
              { t: "Save time and money", d: "Get the work of a full team without the cost of one. We take care of the everyday messages, so your money and your people go where they matter most." },
              { t: "Everything in one place", d: "Run your whole business from one place. Customers, chats, and actions together on WhatsApp, where your customers already are and your team already works." },
              { t: "Win more customers", d: "Reply fast, follow up at the right time, and never let a lead go cold. Engage customers smartly and turn more chats into sales." },
              { t: "Where your customers already are", d: "Your customers use WhatsApp every day. Make it the face of your business, easy for them and easy for you, with no new app to learn." },
            ].map((b, i) => (
              <motion.div key={b.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 3) * 0.06 }}
                className="px-7 py-10" style={{ background: "var(--canvas)" }}>
                <h3 className="text-lg" style={{ fontWeight: 400, color: "var(--text)" }}>{b.t}</h3>
                <p className="mt-2 text-sm md:text-base" style={{ color: "var(--text-2)", fontWeight: 300, lineHeight: 1.6 }}>{b.d}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Set up and control */}
        <section className="px-6 md:px-12 py-20" style={{ borderBottom: "1px solid var(--border)" }}>
          <motion.div {...fadeUp}>
            <h2 className="tracking-tight" style={{ fontWeight: 200, fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "var(--text)", lineHeight: 1.18, maxWidth: 640 }}>
              Easy to set up. Easy to stay in charge.
            </h2>
            <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-muted)", fontWeight: 300, lineHeight: 1.6, maxWidth: 600 }}>
              You do not need a tech team. Set up how you work in plain words, then watch
              everything from one place.
            </p>
          </motion.div>
          <div className="mt-14 grid gap-px" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", background: "var(--border)" }}>
            {[
              { t: "Workflows in plain words", d: "Tell us how you handle bookings, questions, and follow ups. Set it up in minutes, no code and no complicated flow charts." },
              { t: "See what is working", d: "One dashboard shows what customers ask, what is converting, and where you are losing them, so you can fix it fast." },
              { t: "Always in your control", d: "Decide what we can do, who we involve, and how we reply. Change it any time. Nothing happens outside your rules." },
            ].map((b, i) => (
              <motion.div key={b.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                className="px-7 py-10" style={{ background: "var(--canvas)" }}>
                <h3 className="text-lg" style={{ fontWeight: 400, color: "var(--text)" }}>{b.t}</h3>
                <p className="mt-2 text-sm md:text-base" style={{ color: "var(--text-2)", fontWeight: 300, lineHeight: 1.6 }}>{b.d}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Get started */}
        <section className="px-6 md:px-12 py-28 text-center">
          <motion.h2 {...fadeUp} className="tracking-tight mx-auto"
            style={{ fontWeight: 200, fontSize: "clamp(2rem, 5vw, 3.4rem)", color: "var(--text)", maxWidth: 700, lineHeight: 1.14 }}>
            Run your whole business from WhatsApp.
          </motion.h2>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/sign-up" className="btn btn-accent" data-testid="footer-cta" style={{ height: 48, paddingLeft: 28, paddingRight: 28 }}>
              Get started
            </Link>
            <a href="#demo" className="btn" data-testid="footer-demo" style={{ height: 48, paddingLeft: 24, paddingRight: 24, border: "1px solid var(--border-strong)", color: "var(--text)" }}>
              See it in action
            </a>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
